/**
 * Shared map tile provider configuration.
 *
 * Used by all geo-map components (GeoModalContent, GeoIpExpansionPanel,
 * GeoPlaybackHistory, ItemGeoMap, VpnGeoMap) to avoid duplicating URLs
 * and tile settings across files.
 */

export type TileProvider = "theme" | "dark" | "light" | "street" | "satellite";

export type TileConfig = {
  /** `"vector"` = MapLibre theme basemap (no raster `url`); default `"raster"`. */
  kind?: "raster" | "vector";
  url?: string;
  subdomains?: string;
  maxZoom: number;
  maxNativeZoom?: number;
  labelsUrl?: string;
  attribution?: string;
  description?: string;
  label: string;
  icon: string;
};

const ESRI_ATTRIBUTION = "Esri, HERE, Garmin, © OpenStreetMap contributors";
const OSM_ATTRIBUTION = "© OpenStreetMap contributors";

export const TILE_PROVIDERS: Record<TileProvider, TileConfig> = {
  theme: {
    kind: "vector",
    maxZoom: 19,
    description: "Matches the dashboard theme",
    label: "Theme",
    icon: "lucide:palette",
  },
  dark: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    labelsUrl: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
    maxZoom: 19,
    maxNativeZoom: 16,
    attribution: ESRI_ATTRIBUTION,
    label: "Dark",
    icon: "lucide:moon",
  },
  light: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
    labelsUrl: "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}",
    maxZoom: 19,
    maxNativeZoom: 16,
    attribution: ESRI_ATTRIBUTION,
    label: "Light",
    icon: "lucide:sun",
  },
  street: {
    url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
    subdomains: "abc",
    maxZoom: 19,
    attribution: OSM_ATTRIBUTION,
    label: "Street",
    icon: "lucide:map",
  },
  satellite: {
    url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    maxZoom: 18,
    attribution: ESRI_ATTRIBUTION,
    label: "Satellite",
    icon: "lucide:satellite",
  },
};

export const TILE_KEYS = Object.keys(TILE_PROVIDERS) as TileProvider[];

/** Read a persisted tile choice from localStorage, falling back to "dark". */
export const getStoredTile = (storageKey: string): TileProvider => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored && stored in TILE_PROVIDERS) return stored as TileProvider;
  } catch { /* ignore */ }
  return "theme";
};

/** Persist tile choice to localStorage. */
export const storeTile = (storageKey: string, tile: TileProvider): void => {
  try {
    localStorage.setItem(storageKey, tile);
  } catch { /* ignore */ }
};
